import { useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { floorplannerStore } from '../store/floorplannerStore';
import { editorStore } from '../store/editorStore';
import { userStore } from '../store/userStore';
import {
  ClaimFloorplanEditorDocument,
  FloorplanCollabChangedDocument,
  ReleaseFloorplanEditorDocument,
} from '../gql/floorplanCollab';
import { subscribeToGraphQLWsReconnect } from '../utils/graphqlWsReconnect';

const CLAIM_INTERVAL_MS = 15_000;

type CollabState = {
  isMine: boolean;
  updatedAt?: string | null;
  editingBy?: {
    id: string;
    userName?: string | null;
    email?: string | null;
  } | null;
};

type FloorplanCollabEvent = {
  floorplanId: string;
  updatedAt?: string | null;
  editingBy?: CollabState['editingBy'];
};

type GraphQLClaimError = {
  message?: string;
  graphQLErrors?: Array<{
    message?: string;
    extensions?: { code?: string };
  }>;
};

const isAccessRevokedError = (error: unknown): boolean => {
  const err = error as GraphQLClaimError;
  const message = err?.graphQLErrors?.[0]?.message || err?.message || '';
  return (
    message.includes('do not have access') ||
    message.includes('not allowed to edit')
  );
};

/**
 * True when the server's copy is newer than the snapshot held in memory, i.e.
 * another collaborator saved while this tab did not hold the lease. Missing or
 * unparsable timestamps count as newer: freshness we cannot prove must not be
 * assumed, or we would skip the reload that protects their work.
 */
const isRemoteNewer = (
  remoteUpdatedAt: string | null | undefined,
  localUpdatedAt: string | null,
): boolean => {
  if (!remoteUpdatedAt || !localUpdatedAt) return true;
  const remoteTime = new Date(remoteUpdatedAt).getTime();
  const localTime = new Date(localUpdatedAt).getTime();
  if (Number.isNaN(remoteTime) || Number.isNaN(localTime)) return true;
  return remoteTime > localTime;
};

const restoreCamera = (
  camera: typeof editorStore.camera,
  savedPosition: THREE.Vector3 | undefined,
  savedZoom: number | undefined,
) => {
  if (!camera || !savedPosition) return;
  camera.position.copy(savedPosition);
  if (savedZoom != null && 'zoom' in camera) {
    (camera as typeof camera & { zoom: number }).zoom = savedZoom;
    camera.updateProjectionMatrix?.();
  }
};

/**
 * Simple take-over lease for shared floorplans.
 *
 * - Hold the lease → edit and save like a normal (pre-collab) plan.
 * - Someone else holds it → view-only + banner.
 * - Take over → force claim, reload latest saved JSON once, then unlock.
 * - Another editor changes it → lock immediately, then reload the canvas.
 *
 * Heartbeat every 15s only refreshes the lease / lock banner.
 *
 * Websocket change events refresh a view-only canvas immediately. Gaining the
 * lease still verifies that the latest saved JSON is loaded in case an event was
 * missed while disconnected. Without that, the first edit after a hand-off
 * could save an old snapshot over everything the previous editor did.
 */
export function useFloorplanCollab(enabled: boolean) {
  const claimingForIdRef = useRef<string | null>(null);
  const claimingForceRef = useRef(false);
  const releasedRef = useRef(false);
  const reloadGenerationRef = useRef(0);
  const claimGenerationRef = useRef(0);
  /**
   * Version currently being fetched after a websocket event. Duplicate lease
   * heartbeats for that version must not start another multi-megabyte reload.
   */
  const pendingRemoteReloadKeyRef = useRef<string | null>(null);
  /**
   * Floorplan id we last successfully held the lease for.
   * Captured separately from store.collabIsMine so cleanup can still release after
   * loadFromCloud clears lock state during a plan switch.
   */
  const heldLeaseFloorplanIdRef = useRef<string | null>(null);
  /**
   * True when this tab has not held the lease continuously since its last load,
   * so another collaborator may have saved over the snapshot still on screen.
   * Any spell without the lease sets it; only a successful reload clears it.
   */
  const mayBeStaleRef = useRef(false);

  const floorplanId = floorplannerStore.id;

  /** Best-effort lease release, so a tab that cannot edit does not hold others up. */
  const releaseLease = useCallback((releasedFloorplanId: string) => {
    const client = floorplannerStore.client;
    heldLeaseFloorplanIdRef.current = null;
    if (!client) return;
    client
      .mutate({
        mutation: ReleaseFloorplanEditorDocument,
        variables: { floorplanId: releasedFloorplanId },
      })
      .catch(() => {
        /* the 45s server TTL frees the lease anyway */
      });
  }, []);

  const reloadRemoteFloorplan = useCallback(async (claimedFloorplanId: string) => {
    if (!floorplannerStore.id || floorplannerStore.id !== claimedFloorplanId) {
      return false;
    }

    const generation = ++reloadGenerationRef.current;

    const camera = editorStore.camera;
    const savedPosition = camera?.position?.clone();
    const savedZoom = camera && 'zoom' in camera ? (camera as { zoom?: number }).zoom : undefined;

    const applied = await floorplannerStore.loadFromCloud(claimedFloorplanId, {
      abortIfDirty: false,
      preserveCamera: true,
    });

    if (!applied) return false;
    if (generation !== reloadGenerationRef.current) return false;
    if (floorplannerStore.id !== claimedFloorplanId) return false;
    if (floorplannerStore.dirty) return true;

    restoreCamera(camera, savedPosition, savedZoom);
    return true;
  }, []);

  const applyCollabState = useCallback(async (
    state: CollabState,
    claimedFloorplanId: string,
    options?: { force?: boolean },
  ) => {
    if (floorplannerStore.id !== claimedFloorplanId) return;

    const holder = state.editingBy;
    const isMine = Boolean(state.isMine);

    if (!isMine) {
      // View-only. Anything the lease holder saves from here on leaves this
      // canvas behind, so the next hand-off has to reload before we can edit.
      mayBeStaleRef.current = true;
      floorplannerStore.setCollabLock({
        lockedByOther: Boolean(holder),
        editingByName: holder?.userName || holder?.email || null,
        editingById: holder?.id || null,
        isMine: false,
      });
      heldLeaseFloorplanIdRef.current = null;
      return;
    }

    // Reload only when this canvas may not match the latest save.
    // Automatic reclaim: the server timestamp is the proof. Local dirty must
    // not force a reload here — that would throw away this tab's own unsaved
    // work when nobody else saved anything.
    // Take-over: the banner already promises to discard unsaved work, so dirty
    // is enough even when the timestamp has not moved.
    const remoteNewer = isRemoteNewer(
      state.updatedAt,
      floorplannerStore.updatedAt,
    );
    const mustReload = options?.force
      ? remoteNewer || floorplannerStore.dirty
      : mayBeStaleRef.current && remoteNewer;

    // Claim succeeded server-side, so cleanup must release even if the reload
    // below fails or this tab unmounts mid-flight.
    heldLeaseFloorplanIdRef.current = claimedFloorplanId;

    if (mustReload) {
      const reloaded = await reloadRemoteFloorplan(claimedFloorplanId);
      if (floorplannerStore.id !== claimedFloorplanId) return;
      if (!reloaded) {
        // Stay view-only rather than hand edit rights to a stale canvas, and
        // drop the lease so a failed hand-off cannot block the others.
        mayBeStaleRef.current = true;
        floorplannerStore.setCollabReloadFailed();
        releaseLease(claimedFloorplanId);
        return;
      }
    }

    mayBeStaleRef.current = false;
    floorplannerStore.setCollabLock({
      lockedByOther: false,
      editingByName: holder?.userName || holder?.email || null,
      editingById: holder?.id || null,
      isMine: true,
    });
  }, [releaseLease, reloadRemoteFloorplan]);

  const claim = useCallback(
    async (force = false) => {
      if (!enabled) return;
      // Keep the subscription up (same as moodboard/document locks) but do not
      // keep hammering claim after the server has revoked access.
      if (floorplannerStore.collabAccessLost) return;
      const claimFloorplanId = floorplannerStore.id;
      if (!claimFloorplanId || !floorplannerStore.client) return;
      if (claimingForIdRef.current === claimFloorplanId) {
        if (!force || claimingForceRef.current) return;
      }

      const claimGeneration = ++claimGenerationRef.current;
      claimingForIdRef.current = claimFloorplanId;
      claimingForceRef.current = force;
      try {
        const result = await floorplannerStore.client.mutate({
          mutation: ClaimFloorplanEditorDocument,
          variables: { floorplanId: claimFloorplanId, force },
          fetchPolicy: 'no-cache',
        });
        if (claimGeneration !== claimGenerationRef.current) return;
        if (floorplannerStore.id !== claimFloorplanId) return;
        const state = result.data?.claimFloorplanEditor as CollabState | undefined;
        if (state) {
          await applyCollabState(state, claimFloorplanId, { force });
        }
      } catch (error) {
        if (claimGeneration !== claimGenerationRef.current) return;
        if (floorplannerStore.id === claimFloorplanId && isAccessRevokedError(error)) {
          floorplannerStore.setCollabAccessLost();
          heldLeaseFloorplanIdRef.current = null;
          mayBeStaleRef.current = true;
        } else {
          console.warn('Failed to claim floorplan editor lease', error);
        }
      } finally {
        if (claimGeneration === claimGenerationRef.current && claimingForIdRef.current === claimFloorplanId) {
          claimingForIdRef.current = null;
          claimingForceRef.current = false;
        }
      }
    },
    [applyCollabState, enabled],
  );

  const takeOver = useCallback(() => {
    floorplannerStore.invalidateCloudLoads();
    reloadGenerationRef.current += 1;
    void claim(true);
  }, [claim]);

  useEffect(() => {
    if (!enabled || !floorplanId) {
      floorplannerStore.clearCollabLock();
      heldLeaseFloorplanIdRef.current = null;
      return;
    }

    const effectFloorplanId = floorplanId;
    releasedRef.current = false;
    void claim(false);

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void claim(false);
    }, CLAIM_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void claim(false);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const unsubscribeReconnect = subscribeToGraphQLWsReconnect(() => {
      // Missed collab events are not replayed. Mark stale even if hidden so
      // the next visible claim can reload if someone else saved meanwhile.
      mayBeStaleRef.current = true;
      if (document.visibilityState === 'hidden') return;
      void claim(false);
    });

    /**
     * Instant lock/change notifications. Own echoes are ignored — the claim
     * mutation already applied state. Free lease → reclaim; another holder →
     * enter view-only immediately, then refresh only the floorplan canvas.
     */
    const client = floorplannerStore.client;
    const subscription = client
      ? client
          .subscribe<{ floorplanCollabChanged: FloorplanCollabEvent }>({
            query: FloorplanCollabChangedDocument,
            variables: { floorplanId: effectFloorplanId },
          })
          .subscribe({
            next: ({ data }) => {
              const event = data?.floorplanCollabChanged;
              if (!event || floorplannerStore.id !== effectFloorplanId) return;

              const holder = event.editingBy;
              if (holder?.id && holder.id === userStore.userId) {
                return;
              }
              if (!holder) {
                void claim(false);
                return;
              }
              void applyCollabState(
                {
                  isMine: false,
                  updatedAt: event.updatedAt,
                  editingBy: holder,
                },
                effectFloorplanId,
              );

              // Lease heartbeats use the same event as content changes. Update
              // lock state above, but fetch JSON only for a newer server version.
              if (!isRemoteNewer(event.updatedAt, floorplannerStore.updatedAt)) {
                return;
              }

              const reloadKey = `${effectFloorplanId}:${event.updatedAt ?? 'unknown'}`;
              if (pendingRemoteReloadKeyRef.current === reloadKey) {
                return;
              }
              pendingRemoteReloadKeyRef.current = reloadKey;
              void reloadRemoteFloorplan(effectFloorplanId).finally(() => {
                if (pendingRemoteReloadKeyRef.current === reloadKey) {
                  pendingRemoteReloadKeyRef.current = null;
                }
              });
            },
            error: (error) => {
              console.warn('Floorplan collab subscription error', error);
            },
          })
      : null;

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      unsubscribeReconnect();
      subscription?.unsubscribe();
      reloadGenerationRef.current += 1;
      pendingRemoteReloadKeyRef.current = null;
      claimGenerationRef.current += 1;
      claimingForceRef.current = false;
      if (claimingForIdRef.current === effectFloorplanId) {
        claimingForIdRef.current = null;
      }
      const heldId = heldLeaseFloorplanIdRef.current;
      if (!releasedRef.current && heldId && heldId === effectFloorplanId) {
        releasedRef.current = true;
        releaseLease(effectFloorplanId);
      }
      floorplannerStore.clearCollabLock();
    };
  }, [applyCollabState, claim, enabled, floorplanId, releaseLease, reloadRemoteFloorplan]);

  return { takeOver, claim };
}
